import { DraftAccount, DraftGroup } from "../draftState";
import { GetRowCapabilities } from "../editorCapabilities";
import { normalizeGroupName } from "../draftGroupOperations";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    DropEvaluation,
    DropScope,
    evaluateDrop,
    isDescendantOrSelf,
    isAccountInDropScope,
} from "./nestedDragRules";

const rowParentGroupId = (row: NestedEditorRow): string | null =>
    row.account?.parentGroupId ?? row.group?.parentGroupId ?? null;

// From a multi-select, drops any row that is a descendant (in the tree) of
// another selected GROUP: a selected group already carries its whole subtree, so
// its selected descendants must not move separately — this prevents a
// double-move / cycle. Returns the remaining "top-level" rows in their original
// (tree) order, which is the set `moveRows` should actually relocate.
export const resolveTopLevelDraggedRows = ({
    selectedRows,
    groups,
}: {
    selectedRows: NestedEditorRow[];
    groups: DraftGroup[];
}): NestedEditorRow[] => {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const selectedGroupIds = selectedRows
        .filter((row) => row.kind === "group" && row.group)
        .map((row) => row.id);
    return selectedRows.filter((row) => {
        const parentGroupId = rowParentGroupId(row);
        if (parentGroupId == null) {
            return true;
        }
        return selectedGroupIds.every(
            (groupId) =>
                groupId === row.id ||
                !isDescendantOrSelf(parentGroupId, groupId, groupsById),
        );
    });
};

// The de-duplicated top-level rows a multi-select drag moves, or null when this
// is a single-row drag: the active row isn't selected, or fewer than two
// draggable rows are selected. Centralizes the "is this a multi-drag" gate so
// the commit path and the lifted-drag-set preview can't diverge on it.
export const resolveMultiDragTopLevelRows = ({
    activeRow,
    selectedDraggableRows,
    groups,
}: {
    activeRow: NestedEditorRow;
    selectedDraggableRows: NestedEditorRow[];
    groups: DraftGroup[];
}): NestedEditorRow[] | null => {
    const isActiveSelected = selectedDraggableRows.some(
        (row) => row.id === activeRow.id,
    );
    if (!isActiveSelected || selectedDraggableRows.length < 2) {
        return null;
    }
    return resolveTopLevelDraggedRows({
        selectedRows: selectedDraggableRows,
        groups,
    });
};

// All-or-nothing validation for a multi-row drag: returns the first member's
// failure (so the caller can reuse its reason) or { ok: true } when EVERY member
// is a valid move to `targetScope`. Reuses the single-row `evaluateDrop` rules.
export const evaluateRowsDrop = ({
    draggedRows,
    targetScope,
    groups,
    getRowCapabilities,
    accounts,
    destinationChainDepth,
    skipDuplicateName,
}: {
    draggedRows: NestedEditorRow[];
    targetScope: DropScope;
    groups: DraftGroup[];
    getRowCapabilities: GetRowCapabilities;
    accounts: DraftAccount[];
    // A destination parent minted from a remaining name path has a depth the
    // draft groups don't yet reflect.
    destinationChainDepth?: number;
    skipDuplicateName?: boolean;
}): DropEvaluation => {
    for (const draggedRow of draggedRows) {
        const result = evaluateDrop({
            draggedRow,
            targetScope,
            groups,
            getRowCapabilities,
            accounts,
            destinationChainDepth,
            skipDuplicateName,
        });
        if (!result.ok) {
            return result;
        }
    }
    // Per-row `evaluateDrop` only catches collisions against non-moving
    // siblings; it never sees the other rows in the same gesture. Since every
    // dragged row lands in the same `targetScope`, reject when two moving groups
    // share a normalized name (e.g. multi-selecting "Payroll" from two parents
    // and dropping both into one group would create duplicate siblings).
    const seenGroupNames = new Set<string>();
    for (const draggedRow of draggedRows) {
        if (draggedRow.kind !== "group" || !draggedRow.group) {
            continue;
        }
        const normalized = normalizeGroupName(draggedRow.group.name);
        if (seenGroupNames.has(normalized)) {
            return { ok: false, reason: "duplicateName" };
        }
        seenGroupNames.add(normalized);
    }
    // Same for moving accounts, keyed by business so two same-named rows from
    // disjoint businesses landing under equivalent parents stay legal.
    const seenAccountKeys = new Set<string>();
    for (const draggedRow of draggedRows) {
        if (draggedRow.kind !== "account") {
            continue;
        }
        for (const account of draggedRow.accounts) {
            const normalized = normalizeGroupName(account.name);
            if (
                normalized.length === 0 ||
                isAccountInDropScope(account, targetScope)
            ) {
                continue;
            }
            const key = `${account.entityId}::${normalized}`;
            if (seenAccountKeys.has(key)) {
                return { ok: false, reason: "duplicateName" };
            }
            seenAccountKeys.add(key);
        }
    }
    return { ok: true };
};
